import { getUserProfile, renderCount } from 'components/commitsPage/commitsPage';
import {
    CommitsPageContextProvider,
    useCommitsPageContext,
} from 'components/commitsPage/commitsPageContext';
import { useContract } from 'components/contract/contractContext';
import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
} from 'components/settings/collectionContext';
import {
    ContractCommitsCollector,
    ContractCommitsProvider,
} from 'components/settings/contractCommitsContext';
import { DocumentProvider } from 'components/settings/documentContext';
import { orderBy, Timestamp, where } from 'firebase/firestore';
import {
    Commit,
    commitCoverter,
    COMMIT_COLLECTION_NAME,
    contractConverter,
    CONTRACT_COLLECTION_NAME,
    languageConverter,
    LANGUAGE_COLLECTION_NAME,
    UserProfile,
    userProfileCoverter,
    USER_PROFILE_COLLECTION_NAME,
} from 'models';
import { useParams } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import './contractCommitsPage.css';
import { useLanguage } from 'components/contractLanguagesPage/contractLanguagesPage';
import { useAccount } from 'components/settings/userProfileContext';
import { usePathUrl } from 'components/usersPage/usersPage';

export const ContractCommitsPage = () => {
    const { contractId } = useParams();
    if (!contractId) {
        return <div>Cannot load contract with id '${contractId}'</div>;
    }
    return (
        <CollectionProvider collectionName={LANGUAGE_COLLECTION_NAME} converter={languageConverter}>
            <DocumentProvider
                collectionName={CONTRACT_COLLECTION_NAME}
                converter={contractConverter}
                id={contractId}
            >
                <CollectionProvider
                    collectionName={USER_PROFILE_COLLECTION_NAME}
                    converter={userProfileCoverter}
                    keyFunction={(r) => r.email}
                >
                    <CommitsPageContextProvider>
                        <ContractCommitsPageWrapper />
                    </CommitsPageContextProvider>
                </CollectionProvider>
            </DocumentProvider>
        </CollectionProvider>
    );
};

export const ContractCommitsPageWrapper = () => {
    const [contract, pending] = useContract();
    if (pending) {
        return null;
    }
    const indexIds = contract.getIndexIds();
    return (
        <ContractCommitsProvider indexIds={indexIds}>
            <ContractCommitsPageContentProvider />
        </ContractCommitsProvider>
    );
};

export const ContractCommitsPageContentProvider = () => {
    const [contract, pending] = useContract();
    if (pending) {
        return null;
    }
    return (
        <CollectionProvider
            collectionName={COMMIT_COLLECTION_NAME}
            converter={commitCoverter}
            constraints={[
                where('targetIndexId', '==', contract.indexId()),
                orderBy('createdAt', 'desc'),
            ]}
        >
            <ContractCommitsCollector indexIds={contract.getIndexIds()} contents={[]}>
                {(contents: CollectionState[]) => (
                    <ContractCommitsPageContent contents={contents} />
                )}
            </ContractCommitsCollector>
        </CollectionProvider>
    );
};

const getReviewGroups = (reviewCommits: Commit[], commits: Commit[]) => {
    const reviewGroups = reviewCommits.map((commit) => {
        return { commit, commits: [] as Commit[] };
    });
    commits.map((commit) => {
        const reviewGroup = reviewGroups.find(
            (reviewGroup) =>
                reviewGroup.commit.languageIndexId === commit.languageIndexId &&
                (reviewGroup.commit.createdAt as Timestamp) > (commit.createdAt as Timestamp)
        );
        if (reviewGroup) {
            reviewGroup.commits.push(commit);
        }
        return reviewGroup;
    });
    return reviewGroups;
};

export interface ContractCommitsPageContentProps {
    contents: CollectionState[];
}
export const ContractCommitsPageContent = ({ contents }: ContractCommitsPageContentProps) => {
    const [contract, pending] = useContract();
    const { setExpCol, expCol } = useCommitsPageContext();
    const [contractCommits] = useCollectionContext(COMMIT_COLLECTION_NAME, [
        where('targetIndexId', '==', contract.indexId()),
        orderBy('createdAt', 'desc'),
    ]) as CollectionState<Commit>;
    if (pending) {
        return null;
    }
    const reviewCommits = contractCommits.filter(({ action }) => action === 'review');
    const commits = contents.map(([commits]) => commits).flat() as Commit[];
    const reviewGroups = getReviewGroups(reviewCommits, commits);
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#FCFBFE',
                minHeight: '100vh',
                width: '100vw',
            }}
        >
            <div style={{ fontSize: '34px', margin: '20px 0' }}>Change History</div>
            <div
                style={{
                    width: '100%',
                    height: '70px',
                    borderColor: '#EBEDEE',
                    borderStyle: 'solid',
                    borderWidth: '1px 0 1px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <div style={{ width: '240px', margin: '0 128px' }}></div>
                <div
                    style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0 128px',
                    }}
                    onClick={() => {
                        const nextAllExpCol =
                            expCol.getValue('all') !== 'expanded' ? 'expanded' : 'collapsed';
                        setExpCol('all', nextAllExpCol);
                    }}
                >
                    {expCol.getValue('all') !== 'expanded' ? 'Expand all' : 'Collapse All'}
                </div>
            </div>
            <div style={{ width: 'calc(100% - 256px)', margin: '76px 128px 0 128px' }}>
                {reviewGroups
                    .filter(({ commits }) => commits.length)
                    .map(({ commit, commits }) => (
                        <ReviewGroup key={commit.id} commit={commit} commits={commits} />
                    ))}
            </div>
        </div>
    );
};

const ReviewGroup = ({ commit, commits }: { commit: Commit; commits: Commit[] }) => {
    const { expCol, setExpCol } = useCommitsPageContext();
    const key = commit.id;
    const expanded = expCol.getValue(key) === 'expanded';
    const account = useAccount();
    const language = useLanguage(commit.languageIndexId || account.markets[0].languageIndexId);
    const date = new Date(
        new Date((commit.createdAt?.seconds as number) * 1000).setHours(0, 0, 0, 0)
    );
    return (
        <div style={{ width: '100%' }}>
            <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                <div
                    style={{
                        height: '32px',
                        width: '108px',
                        borderRadius: '15px',
                        backgroundColor: '#18A0FB',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: 'white',
                        marginBottom: '20px',
                        cursor: 'default',
                    }}
                >
                    {language?.name}
                    <div
                        style={{
                            display: 'flex',
                            marginBottom: '-1px',
                            marginRight: '-10px',
                            cursor: 'pointer',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpCol(
                                key,
                                expCol.getValue(key) === 'expanded' ? 'collapsed' : 'expanded'
                            );
                        }}
                    >
                        {expanded ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'center',
                    }}
                >
                    <div style={{ marginTop: '-18px', marginLeft: '16px' }}>
                        <div style={{ display: 'flex' }}>
                            {renderCount(commits.length, 'entry', 'entries')} modified
                        </div>
                        <div
                            style={{ color: 'rgb(27 27 27 / 50%)' }}
                        >{`${date.getDate()}/${date.getMonth()}/${date.getFullYear()}`}</div>
                    </div>
                </div>
            </div>
            {expanded && <ReviewCommit commit={commit} commits={commits} />}
        </div>
    );
};

const ReviewCommit = ({ commit, commits }: { commit: Commit; commits: Commit[] }) => {
    const { expCol, setExpCol } = useCommitsPageContext();
    const key = `${commit.id}-review`;
    const expanded = expCol.getValue(key) === 'expanded';
    const [, , , userProfileMap] = useCollectionContext(
        USER_PROFILE_COLLECTION_NAME
    ) as CollectionState<UserProfile>;
    const userProfile = getUserProfile(userProfileMap, commit.createdBy);
    const downloadUrl = usePathUrl(userProfile?.profileImages?.[0]?.path);
    const latestDate = new Date(
        new Date((commits[0].createdAt?.seconds as number) * 1000).setHours(0, 0, 0, 0)
    );
    const earliestDate = new Date(
        new Date((commits.slice(-1)[0].createdAt?.seconds as number) * 1000).setHours(0, 0, 0, 0)
    );
    return (
        <div style={{ marginBottom: '20px', border: '1px solid #D8D8D8' }}>
            <div
                key={commit.id}
                style={{ height: '48px', backgroundColor: '#FFFFFF', display: 'flex' }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100px',
                        height: '100%',
                        cursor: 'pointer',
                        paddingLeft: '8px',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpCol(
                            key,
                            expCol.getValue(key) === 'expanded' ? 'collapsed' : 'expanded'
                        );
                    }}
                >
                    {renderCount(commits.length, 'entry', 'entries')}
                    {expanded ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexBasis: '100px',
                        flexGrow: 1,
                        height: '100%',
                    }}
                >
                    {`${earliestDate.getDate()}/${earliestDate.getMonth()}/${earliestDate.getFullYear()}`}
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexBasis: '100px',
                        flexGrow: 1,
                        height: '100%',
                    }}
                >
                    {`${latestDate.getDate()}/${latestDate.getMonth()}/${latestDate.getFullYear()}`}
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexBasis: '100px',
                        flexGrow: 1,
                        height: '100%',
                    }}
                >
                    <img
                        alt=""
                        src={downloadUrl || '/Ellipse4.png'}
                        height="40px"
                        width="40px"
                        style={{ marginTop: '-0px', borderRadius: '60px', marginRight: '20px' }}
                    />
                    {userProfile?.name}
                </div>
            </div>
            {expanded &&
                commits.map((commit, index) => {
                    const commitDate = new Date((commit.createdAt?.seconds as number) * 1000);
                    const minutes = `${commitDate.getMinutes()}`.padStart(2, '0');
                    const hours = commitDate.getHours();
                    const changes = commit.changes.filter((change) =>
                        commit.typeIndex.endsWith('Clause')
                            ? change.field === 'content'
                            : change.field === 'name'
                    );
                    if (!changes.length) {
                        return null;
                    }
                    const change = changes[0];
                    return (
                        <div
                            key={commit.id}
                            style={{
                                minHeight: '48px',
                                backgroundColor: '#FFFFFF',
                                borderWidth: `${index === 0 ? '1px' : '1px'} 0 0 0`,
                                borderColor: '#D8D8D8',
                                borderStyle: 'solid',
                                display: 'flex',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                    width: '100px',
                                    height: '100%',
                                }}
                            >
                                <div>
                                    {`${commitDate.getDate()}/${commitDate.getMonth()}/${commitDate.getFullYear()}`}{' '}
                                </div>{' '}
                                <div
                                    style={{ color: 'rgb(27 27 27 / 50%)' }}
                                >{`${hours}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`}</div>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexBasis: '100px',
                                    flexGrow: 1,
                                    height: '100%',
                                }}
                            >
                                {commit.message}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexBasis: '100px',
                                    flexGrow: 1,
                                    height: '100%',
                                    whiteSpace: 'break-spaces',
                                }}
                            >
                                {change.before}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexBasis: '100px',
                                    flexGrow: 1,
                                    height: '100%',
                                    whiteSpace: 'break-spaces',
                                }}
                            >
                                {change.after}
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};
